import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { Anchor } from '@atlaskit/primitives/compiled';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { CONTENT_URL_3P_ACCOUNT_AUTH, CONTENT_URL_SECURITY_AND_PERMISSIONS } from '../../constants';
import { messages } from '../../messages';
import { isNewBlockcardUnauthorizedRefreshExperimentEnabled } from '../../utils/experiments';

type UnauthorisedViewContentProps = {
	/**
	 * If `true`, display an alternative message which prompts user to connect all
	 * Atlassian products (vs. smart links only) to the 3rd party account.
	 */
	isProductIntegrationSupported?: boolean;

	/**
	 * Is the name of the provider for the account that needs to be connected
	 * Eg. Google, Microsoft etc
	 */
	providerName?: string;

	/**
	 * A `testId` prop is provided for specified elements, which is a unique
	 * string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests
	 */
	testId?: string;
};

/**
 * This component is used in unauthorized views of a smart link.
 * It represents the main text that provides a user with information on how to connect their account
 */
const UnauthorisedViewContent = ({
	providerName,
	isProductIntegrationSupported,
	testId = 'unauthorised-view-content',
}: UnauthorisedViewContentProps): React.JSX.Element => {
	const { fireEvent } = useAnalyticsEvents();

	const handleLearnMoreClick = useCallback(() => {
		fireEvent('ui.button.clicked.learnMore', {});
	}, [fireEvent]);

	const learnMoreMessage = isProductIntegrationSupported
		? fg('product-terminology-refresh')
			? messages.learn_more_about_connecting_account_appify
			: messages.learn_more_about_connecting_account
		: messages.learn_more_about_smart_links;

	const unauthorizedAccountDescriptionMessage = isNewBlockcardUnauthorizedRefreshExperimentEnabled()
		? messages.learn_more_about_connecting_account_experiment_shorter
		: fg('product-terminology-refresh')
			? messages.connect_unauthorised_account_description_appify
			: messages.connect_unauthorised_account_description;

	return (
		<>
			{providerName ? (
				<FormattedMessage
					{...unauthorizedAccountDescriptionMessage}
					values={{ context: providerName }}
				/>
			) : (
				<FormattedMessage
					{...(fg('product-terminology-refresh')
						? messages.connect_unauthorised_account_description_no_provider_appify
						: messages.connect_unauthorised_account_description_no_provider)}
				/>
			)}
			{isNewBlockcardUnauthorizedRefreshExperimentEnabled() ? <br /> : ' '}
			<Anchor
				href={
					isProductIntegrationSupported
						? CONTENT_URL_3P_ACCOUNT_AUTH
						: CONTENT_URL_SECURITY_AND_PERMISSIONS
				}
				target="_blank"
				testId={`${testId}-learn-more`}
				onClick={handleLearnMoreClick}
			>
				<FormattedMessage {...learnMoreMessage} />
			</Anchor>
		</>
	);
};

export default UnauthorisedViewContent;
